import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { promptsApi } from '../api/prompts';
import type { Prompt } from '../types';

/**
 * Hook for fetching prompt list (lightweight, no content)
 */
export function usePromptList() {
  const {
    data: prompts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['prompts'],
    queryFn: promptsApi.list,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Derive active prompt from the list
  const activePrompt = prompts.find((p) => p.is_active) || null;

  return {
    prompts,
    activePrompt,
    isLoading,
    error,
  };
}

/**
 * Hook for fetching a single prompt's full content
 */
export function usePromptDetail(promptId: string | null) {
  const {
    data: prompt,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['prompt', promptId],
    queryFn: () => promptsApi.get(promptId!),
    enabled: !!promptId, // Only fetch when promptId is provided
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    prompt: prompt || null,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for prompt mutations (create, update, delete, setActive)
 */
export function usePromptMutations() {
  const queryClient = useQueryClient();

  // Mutation for creating prompt
  const createMutation = useMutation({
    mutationFn: (data: { name: string; system_prompt: string; user_prompt: string }) =>
      promptsApi.create(data),
    onSuccess: async (newPrompt) => {
      // Invalidate list and add the new prompt to cache
      await queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.setQueryData(['prompt', String(newPrompt.id)], newPrompt);
      return newPrompt;
    },
  });

  // Mutation for updating prompt
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Prompt> }) =>
      promptsApi.update(id, updates),
    onSuccess: (updatedPrompt, { id }) => {
      // Update both list and detail cache
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.setQueryData(['prompt', id], updatedPrompt);
    },
  });

  // Mutation for deleting prompt
  const deleteMutation = useMutation({
    mutationFn: (id: string) => promptsApi.delete(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.removeQueries({ queryKey: ['prompt', id] });
    },
  });

  // Mutation for setting active prompt
  const setActiveMutation = useMutation({
    mutationFn: (id: string) => promptsApi.setActive(id),
    onMutate: async (newActiveId) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['prompts'] });

      // Snapshot the previous value
      const previousPrompts = queryClient.getQueryData(['prompts']);

      // Optimistically update to the new value
      queryClient.setQueryData(['prompts'], (old: any[] | undefined) => {
        if (!old) return old;
        return old.map((prompt) => ({
          ...prompt,
          is_active: String(prompt.id) === newActiveId,
        }));
      });

      // Return a context object with the snapshotted value
      return { previousPrompts };
    },
    onError: (_err, _newActiveId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousPrompts) {
        queryClient.setQueryData(['prompts'], context.previousPrompts);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure server state is correct
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
    },
  });

  return {
    createPrompt: createMutation.mutateAsync,
    updatePrompt: (id: string, updates: Partial<Prompt>) => updateMutation.mutate({ id, updates }),
    updatePromptAsync: (id: string, updates: Partial<Prompt>) => updateMutation.mutateAsync({ id, updates }),
    deletePrompt: deleteMutation.mutate,
    setActive: setActiveMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSettingActive: setActiveMutation.isPending,
  };
}

/**
 * Combined hook for backward compatibility (if needed)
 */
export function usePrompts() {
  const { prompts, activePrompt, isLoading, error } = usePromptList();
  const mutations = usePromptMutations();

  return {
    prompts,
    activePrompt,
    isLoading,
    error,
    ...mutations,
  };
}
